import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, Mail, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/ui/toast";
import { AuthError } from "../lib/auth";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Logo,
} from "../components/ui";
import { PasswordInput } from "../components/forms/PasswordInput";
import { cn } from "@/lib/utils";

type LoginMethod = "passkey" | "password";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("passkey");
  const [isLoading, setIsLoading] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState("");

  const { loginWithPasskey, loginWithPassword } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockoutEndTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = lockoutEndTime.getTime() - now.getTime();

      if (diff <= 0) {
        setLockoutEndTime(null);
        setCountdown("");
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setCountdown(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockoutEndTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      addToast({
        variant: "destructive",
        title: t("common.error"),
        description: t("auth.email") + " " + t("common.required").toLowerCase(),
      });
      return;
    }

    if (loginMethod === "password" && !password) {
      addToast({
        variant: "destructive",
        title: t("common.error"),
        description:
          t("auth.password") + " " + t("common.required").toLowerCase(),
      });
      return;
    }

    setIsLoading(true);

    try {
      if (loginMethod === "passkey") {
        await loginWithPasskey(email);
      } else {
        await loginWithPassword(email, password);
      }
      addToast({
        variant: "success",
        title: t("auth.loginSuccess"),
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.code === "ACCOUNT_LOCKED" && err.lockedUntil) {
          setLockoutEndTime(new Date(err.lockedUntil));
          addToast({
            variant: "destructive",
            title: t("auth.accountLocked"),
          });
        } else {
          addToast({
            variant: "destructive",
            title: t("common.error"),
            description: err.message,
          });
        }
      } else if (err instanceof Error) {
        addToast({
          variant: "destructive",
          title: t("common.error"),
          description: err.message,
        });
      } else {
        addToast({
          variant: "destructive",
          title: t("auth.loginError"),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLoginMethod = () => {
    setLoginMethod(loginMethod === "passkey" ? "password" : "passkey");
    setPassword("");
  };

  const isLocked = !!lockoutEndTime;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo and branding */}
        <div className="flex flex-col items-center space-y-2 text-center">
          <Logo size="xl" className="mb-2" />
          <p className="text-sm text-muted-foreground">
            Sistema de Gestion
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">
              {t("auth.login")}
            </CardTitle>
            <CardDescription>
              {loginMethod === "passkey"
                ? "Usa tu passkey para acceder de forma segura"
                : "Ingresa tu correo y contrasena"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Lockout warning */}
              {isLocked && (
                <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-destructive">
                      {t("auth.accountLocked")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("auth.accountLockedUntil", { time: countdown })}
                    </p>
                  </div>
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    autoComplete="email webauthn"
                    required
                    disabled={isLocked}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Password field (conditional) */}
              {loginMethod === "password" && (
                <PasswordInput
                  label={t("auth.password")}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  autoComplete="current-password"
                  required
                  disabled={isLocked}
                />
              )}

              {/* Submit button */}
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isLocked || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Autenticando...
                  </>
                ) : loginMethod === "passkey" ? (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    {t("auth.passkeyLogin")}
                  </>
                ) : (
                  t("auth.login")
                )}
              </Button>

              {/* Toggle login method */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    o
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={toggleLoginMethod}
                disabled={isLocked}
              >
                {loginMethod === "passkey" ? (
                  t("auth.passwordLogin")
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    {t("auth.passkeyLogin")}
                  </>
                )}
              </Button>

              {/* Forgot password link */}
              {loginMethod === "password" && (
                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    className={cn(
                      "text-sm text-muted-foreground hover:text-primary hover:underline",
                      isLocked && "pointer-events-none opacity-50"
                    )}
                  >
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
              )}

              {/* Contact admin note */}
              <p className="text-center text-xs text-muted-foreground">
                Si no tienes una cuenta, contacta con administracion.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Version footer */}
        <p className="text-center text-xs text-muted-foreground">
          {__COMMIT_HASH__}
        </p>
      </div>
    </div>
  );
}
